import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

const Hero: React.FC = () => {
  const isClient = typeof window !== 'undefined';
  const [ScrollLink, setScrollLink] = useState<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (isClient) {
      import('react-scroll').then((module) => {
        setScrollLink(() => module.Link);
      });
    }
    
    // Trigger load animation
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [isClient]);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-sb-field-path="sections.0"
    >
      {/* Logo with enhanced reveal animation */}
      <motion.div
        className="absolute top-8 left-8 z-20"
        initial={{ opacity: 0, scale: 0.8, y: -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
        whileHover={{ 
          scale: 1.1,
          y: -3,
          filter: "drop-shadow(0 0 25px rgba(107, 114, 128, 0.6))"
        }}
      >
        <motion.div
          className="relative"
          animate={{
            filter: [
              "drop-shadow(0 0 20px rgba(107, 114, 128, 0.4))",
              "drop-shadow(0 0 30px rgba(107, 114, 128, 0.6))",
              "drop-shadow(0 0 20px rgba(107, 114, 128, 0.4))"
            ]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <motion.img
            src="/MI.png"
            alt="Manifest Illusions"
            className="w-16 h-16 cursor-pointer relative z-10"
            style={{
              filter: "drop-shadow(0 8px 16px rgba(107, 114, 128, 0.4)) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))",
            }}
            initial={{ rotateY: 0, scale: 0.8 }}
            animate={{ rotateY: [0, 5, 0, -5, 0], scale: 1 }}
            transition={{
              rotateY: {
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              },
              scale: {
                duration: 1,
                delay: 0.5,
                ease: "easeOut"
              }
            }}
          />
          
          {/* Enhanced embossed effects */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(107, 114, 128, 0.2) 0%, transparent 70%)',
              filter: 'blur(15px)',
              transform: 'translateZ(-1px)'
            }}
          />
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 60%)',
              filter: 'blur(8px)',
              transform: 'translateZ(1px)'
            }}
          />
        </motion.div>
      </motion.div>

      {/* Background with slow fade-in animation */}
      <motion.div 
        className="absolute inset-0" 
        style={{ 
          background: 'linear-gradient(135deg, #fefefe 0%, #f8f9fa 25%, #f1f3f4 50%, #fefefe 75%, #f8f9fa 100%)'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoaded ? 1 : 0 }}
        transition={{ duration: 2, ease: "easeInOut" }}
      >
        {/* Subtle texture overlay */}
        <motion.div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              radial-gradient(circle at 30% 30%, rgba(107, 114, 128, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 70% 70%, rgba(107, 114, 128, 0.03) 0%, transparent 50%),
              radial-gradient(circle at 50% 50%, rgba(156, 163, 175, 0.02) 0%, transparent 50%)
            `,
            backgroundSize: '500px 500px, 300px 300px, 200px 200px',
            backgroundPosition: '0 0, 150px 150px, 300px 300px',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.03 }}
          transition={{ duration: 2.5, delay: 0.5, ease: "easeInOut" }}
        />
        
        {/* Clean minimalist gradient overlay */}
        <motion.div 
          className="absolute inset-0 opacity-[0.15]"
          style={{
            background: 'linear-gradient(45deg, rgba(107, 114, 128, 0.02) 0%, transparent 40%, rgba(107, 114, 128, 0.04) 80%, transparent 100%)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.15 }}
          transition={{ duration: 3, delay: 1, ease: "easeInOut" }}
        />

        {/* Ash gray elegant accents */}
        <motion.div
          className="absolute top-0 left-0 w-full h-px"
          style={{
            background: 'linear-gradient(to right, transparent, rgba(107, 114, 128, 0.3), transparent)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5
          }}
        />
        
        <motion.div
          className="absolute bottom-0 right-0 w-full h-px"
          style={{
            background: 'linear-gradient(to left, transparent, rgba(107, 114, 128, 0.3), transparent)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5.5
          }}
        />

        {/* Light shimmer effect */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03))',
            width: '100%',
            height: '100%',
          }}
          initial={{ x: '-100%' }}
          animate={{ x: '100%' }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear",
            delay: 2,
            repeatDelay: 4
          }}
        />

        {/* Floating particles */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            className="absolute rounded-full"
            style={{
              left: `${15 + i * 12}%`,
              top: `${20 + i * 10}%`,
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              background: i % 3 === 0 ? '#6b7280' : i % 3 === 1 ? '#9ca3af' : '#4b5563',
              filter: 'blur(0.5px)',
              boxShadow: '0 0 15px rgba(107, 114, 128, 0.6)',
            }}
            initial={{ opacity: 0, y: 0 }}
            animate={{
              opacity: [0, 0.4, 0.9, 0.4],
              scale: [0.8, 1.2, 1.5, 0.8],
              y: [0, -30, -60, -30, 0],
              x: [0, Math.random() * 20 - 10, Math.random() * 40 - 20, 0],
            }}
            transition={{
              duration: 12 + Math.random() * 8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 8 + 2,
            }}
          />
        ))}

        {/* Soft ash gray ambient lighting */}
        <motion.div
          className="absolute top-1/6 left-1/6 w-72 h-72 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(107, 114, 128, 0.04) 0%, rgba(107, 114, 128, 0.01) 40%, transparent 70%)',
            filter: 'blur(120px)',
          }}
          initial={{ opacity: 0 }}
          animate={{
            x: [0, 25, 0],
            y: [0, -18, 0],
            opacity: [0, 0.3, 0.6, 0.3],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />

        <motion.div
          className="absolute bottom-1/6 right-1/6 w-56 h-56 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(156, 163, 175, 0.03) 0%, rgba(156, 163, 175, 0.008) 50%, transparent 70%)',
            filter: 'blur(100px)',
          }}
          initial={{ opacity: 0 }}
          animate={{
            x: [0, -22, 0],
            y: [0, 15, 0],
            opacity: [0, 0.2, 0.4, 0.2],
            scale: [1.2, 0.8, 1.2],
          }}
          transition={{
            duration: 34,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 18,
          }}
        />

        {/* Additional ash gray ambient orbs */}
        <motion.div
          className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(107, 114, 128, 0.04) 0%, rgba(107, 114, 128, 0.01) 40%, transparent 70%)',
            filter: 'blur(160px)',
          }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 0.3, 0.6, 0.3],
            scale: [1, 1.2, 1],
            x: [0, 20, 0],
            y: [0, -15, 0],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />

        <motion.div
          className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(156, 163, 175, 0.03) 0%, rgba(156, 163, 175, 0.008) 50%, transparent 70%)',
            filter: 'blur(140px)',
          }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 0.2, 0.4, 0.2],
            scale: [1.1, 0.9, 1.1],
            x: [0, -18, 0],
            y: [0, 12, 0],
          }}
          transition={{
            duration: 26,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 14,
          }}
        />

        {/* Additional atmospheric elements */}
        <motion.div
          className="absolute top-1/3 left-1/2 w-48 h-48 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(107, 114, 128, 0.02) 0%, transparent 85%)',
            filter: 'blur(90px)',
          }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 0.1, 0.2, 0.1],
            scale: [0.9, 1.4, 0.9],
          }}
          transition={{
            duration: 36,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 22,
          }}
        />

        <motion.div
          className="absolute bottom-1/3 right-1/2 w-40 h-40 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(156, 163, 175, 0.015) 0%, transparent 90%)',
            filter: 'blur(80px)',
          }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 0.08, 0.15, 0.08],
            scale: [1.3, 0.7, 1.3],
          }}
          transition={{
            duration: 42,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 30,
          }}
        />

        {/* Clean grid pattern */}
        <motion.div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(107, 114, 128, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(107, 114, 128, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '100px 100px',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.015 }}
          transition={{ duration: 4, delay: 2, ease: "easeInOut" }}
        />

        {/* Minimalist ash gray reflections */}
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={`reflection-${i}`}
            className="absolute rounded-full"
            style={{
              left: `${25 + i * 18}%`,
              top: `${35 + i * 8}%`,
              width: `${1 + Math.random() * 1.5}px`,
              height: `${1 + Math.random() * 1.5}px`,
              background: 'rgba(107, 114, 128, 0.4)',
              filter: 'blur(0.3px)',
            }}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.2, 0.7, 0.2],
              scale: [0.6, 1.2, 0.6],
            }}
            transition={{
              duration: 6 + Math.random() * 3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 5 + 3,
            }}
          />
        ))}

        {/* Subtle floating elements */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(107, 114, 128, 0.02) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
          initial={{ opacity: 0 }}
          animate={{
            x: [0, 20, 0],
            y: [0, -15, 0],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3,
          }}
        />

        <motion.div
          className="absolute bottom-1/4 right-1/4 w-24 h-24 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(156, 163, 175, 0.015) 0%, transparent 70%)',
            filter: 'blur(30px)',
          }}
          initial={{ opacity: 0 }}
          animate={{
            x: [0, -15, 0],
            y: [0, 10, 0],
            opacity: [0.08, 0.15, 0.08],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 13,
          }}
        />

        {/* Matte finish overlay */}
        <motion.div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(248, 249, 250, 0.05) 100%)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.02 }}
          transition={{ duration: 3, delay: 2.5, ease: "easeInOut" }}
        />
      </motion.div>

      {/* Content with enhanced text animations */}
      <div className="relative z-10">
        <motion.div
          className="flex flex-col items-center space-y-4"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1, delay: 1.5, ease: "easeOut" }}
        >
          <motion.h1 
            className="text-8xl italic" 
            style={{ color: '#374151' }}
            initial={{ opacity: 0, letterSpacing: '-5px' }}
            animate={{ opacity: 1, letterSpacing: '2px' }}
            transition={{ duration: 3, delay: 2, ease: "easeOut" }}
          >
            Manifest
          </motion.h1>
          <motion.h1 
            className="text-8xl italic" 
            style={{ color: '#6b7280' }}
            initial={{ opacity: 0, letterSpacing: '-5px' }}
            animate={{ opacity: 1, letterSpacing: '2px' }}
            transition={{ duration: 3, delay: 2.3, ease: "easeOut" }}
          >
            Illusions
          </motion.h1>
        </motion.div>

        <motion.p
          className="text-xl mt-8 mb-12 max-w-3xl mx-auto text-center"
          data-sb-field-path="sections.0.content"
          initial={{ x: '-100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 1.5, delay: 3, ease: "easeOut" }}
          style={{ color: '#4b5563' }}
        >
       Be Unforgettable in a World That Forgets Everything
        </motion.p>

        <motion.div
          className="flex justify-center gap-6"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 3.5, ease: "easeOut" }}
        >
          {isClient && ScrollLink ? (
            <ScrollLink to="services" smooth={true} duration={500}>
              <motion.button
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 15px 30px rgba(107, 114, 128, 0.25)",
                  background: 'linear-gradient(135deg, #4b5563 0%, #374151 100%)'
                }}
                whileTap={{ scale: 0.95 }}
                className="relative px-8 py-3 rounded-lg transition-all font-medium overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #374151 0%, #4b5563 100%)',
                  boxShadow: '0 4px 15px rgba(107, 114, 128, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                }}
              >
                <motion.div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                  }}
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 2,
                    ease: "easeInOut"
                  }}
                />
                <span className="relative z-10">Explore Services</span>
              </motion.button>
            </ScrollLink>
          ) : (
            <a href="#services">
              <motion.button
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 15px 30px rgba(107, 114, 128, 0.25)",
                  background: 'linear-gradient(135deg, #4b5563 0%, #374151 100%)'
                }}
                whileTap={{ scale: 0.95 }}
                className="relative px-8 py-3 rounded-lg transition-all font-medium overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #374151 0%, #4b5563 100%)',
                  boxShadow: '0 4px 15px rgba(107, 114, 128, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                }}
              >
                <motion.div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                  }}
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 2,
                    ease: "easeInOut"
                  }}
                />
                <span className="relative z-10">Explore Services</span>
              </motion.button>
            </a>
          )}
          
          {isClient && ScrollLink ? (
            <ScrollLink to="custom-design" smooth={true} duration={500}>
              <motion.button
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 10px 25px rgba(107, 114, 128, 0.2)",
                  borderColor: 'rgba(107, 114, 128, 0.6)',
                  background: 'rgba(255, 255, 255, 0.9)'
                }}
                whileTap={{ scale: 0.95 }}
                className="relative px-8 py-3 rounded-lg transition-all font-medium overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(107, 114, 128, 0.2)',
                  color: '#374151',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <motion.div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(107, 114, 128, 0.1), transparent)',
                  }}
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 3,
                    ease: "easeInOut"
                  }}
                />
                <span className="relative z-10">Why Us</span>
              </motion.button>
            </ScrollLink>
          ) : (
            <a href="#why-us">
              <motion.button
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 10px 25px rgba(107, 114, 128, 0.2)",
                  borderColor: 'rgba(107, 114, 128, 0.6)',
                  background: 'rgba(255, 255, 255, 0.9)'
                }}
                whileTap={{ scale: 0.95 }}
                className="relative px-8 py-3 rounded-lg transition-all font-medium overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(107, 114, 128, 0.2)',
                  color: '#374151',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <motion.div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(107, 114, 128, 0.1), transparent)',
                  }}
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 3,
                    ease: "easeInOut"
                  }}
                />
                <span className="relative z-10">Why Us</span>
              </motion.button>
            </a>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;